<template>
  <Bar :data="chartData" :options="chartOptions" />
</template>

<script lang="ts" setup>
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import { Data, Series, SeriesConfig, SeriesOrderConfiguration } from '@/features/reports/models';
import ColourService from '@/shared/services/color';
import { computed, ref, PropType } from 'vue';
import type { ChartData } from 'chart.js';
import merge from 'lodash.merge'; // Import Chart.js 4 types

const props = defineProps({
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  reportId: {
    type: String,
    required: true,
  },
  seriesOrder: {
    type: Array as PropType<SeriesOrderConfiguration>,
    required: true,
  },
  stacked: {
    type: Boolean,
    default: false,
  },
});

const renderSeriesInReverse = ref(false);

const chartData = computed(() => mapData(props.data));

const chartOptions = computed(() => {
  const overrides = {
    plugins: {
      inlineLabels: { reportId: props.reportId, reverseLabels: renderSeriesInReverse.value },
      crosshair: false,
      legend: { position: 'right', reverse: renderSeriesInReverse.value }, // Moved to plugins.legend
    },
    scales: {
      x: {
        title: { display: true, text: props.data.scale.title },
        ticks: { maxTicksLimit: 32 },
        grid: { display: false },
        stacked: props.stacked,
      },
      y: {
        min: 0,
        ticks: {
          callback: (value: any) => (value % 1 === 0 ? value : undefined),
        },
        stacked: props.stacked,
      },
    },
  };

  return merge({}, ChartConfig.gridChartsGlobalOptions, overrides);
});

function mapData(data: Data): ChartData<'bar'> {
  const backgroundPalette = ChartConfig.borderPalette(seriesRenderOrder.value);
  const borderPalette = ChartConfig.borderPalette(seriesRenderOrder.value);

  return {
    labels: data.scale.labels,
    datasets: orderedSeries.value.map((series, index) => ({
      label: getSeriesConfig(series.name).displayName,
      data: series.data,
      backgroundColor: backgroundPalette[index],
      borderColor: borderPalette[index],
      hoverBackgroundColor: ColourService.setAlpha(backgroundPalette[index], 0.4),
      borderWidth: 1,
    })),
  };
}

const orderedSeries = computed(() => seriesRenderOrder.value.map((seriesConfig) => getSeries(seriesConfig.name)));

function getSeries(name: string): Series {
  return props.data.series.find((series) => series.name === name)!; // Use find instead of filter for single result
}

function getSeriesConfig(name: string): SeriesConfig {
  return seriesConfig.value.find((config) => config.name === name)!; // Use find instead of filter
}

const seriesConfig = computed<SeriesConfig[]>(() => {
  if (!props.seriesOrder.length) return [];
  if (typeof props.seriesOrder[0] === 'string') {
    return (props.seriesOrder as string[]).map((seriesName) => ({ name: seriesName, displayName: seriesName }));
  }
  return props.seriesOrder as SeriesConfig[];
});

const seriesRenderOrder = computed(() =>
  renderSeriesInReverse.value ? [...seriesConfig.value].reverse() : seriesConfig.value,
);
</script>
